import { useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  TableCell,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import CustomTable from "../../components/CustomTable";
import { StyledTableCell } from "../../components/styled";
import {
  createTeam,
  deleteTeam,
  getTeams,
  updateTeam,
} from "../../services/team.service";
import {
  normalizeFieldErrors,
  validatePngFile,
  useImageUploadField,
} from "../../utils";

const MAX_SHORT_NAME = 10;
const LOGO_COL_WIDTH_PX = 120;
const LOGO_SIZE_PX = 32;

const TeamLogo = ({ name, logo }) => {
  const [failed, setFailed] = useState(false);

  if (!logo || failed) {
    return (
      <Box
        sx={{
          width: LOGO_SIZE_PX,
          height: LOGO_SIZE_PX,
          borderRadius: 1,
          bgcolor: "action.hover",
        }}
      />
    );
  }

  return (
    <Link
      href={logo}
      target="_blank"
      rel="noreferrer"
      aria-label={name ? `${name} logo` : "Team logo"}
    >
      <img
        src={logo}
        alt=""
        onError={() => setFailed(true)}
        style={{
          display: "block",
          width: LOGO_SIZE_PX,
          height: LOGO_SIZE_PX,
          objectFit: "contain",
        }}
        loading="lazy"
      />
    </Link>
  );
};

const Teams = () => {
  const queryClient = useQueryClient();
  const [sortOrder, setSortOrder] = useState("ASC");
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("create"); // create | edit
  const [editingTeam, setEditingTeam] = useState(null);

  const [name, setName] = useState("");
  const [shortName, setShortName] = useState("");
  const [formError, setFormError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  const imageField = useImageUploadField({
    mode,
    currentUrl: editingTeam?.logo,
  });

  const resetForm = () => {
    setName("");
    setShortName("");
    setFormError("");
    setFieldErrors({});
    imageField.reset();
  };

  const closeDialog = () => {
    setOpen(false);
    setEditingTeam(null);
    resetForm();
  };

  const openCreate = () => {
    setMode("create");
    setEditingTeam(null);
    resetForm();
    setOpen(true);
  };

  const openEdit = (team) => {
    setMode("edit");
    setEditingTeam(team);
    setName(team?.name ?? "");
    setShortName(team?.shortName ?? "");
    setFormError("");
    setFieldErrors({});
    imageField.reset();
    setOpen(true);
  };

  const toggleSortOrder = () => {
    setSortOrder((prev) => (prev === "ASC" ? "DESC" : "ASC"));
  };

  const listQueryKey = useMemo(() => ["teams"], []);

  const { mutate: createNewTeam, isPending: isCreatePending } = useMutation({
    mutationKey: ["createTeam"],
    mutationFn: (payload) => createTeam(payload),
    onSuccess: () => {
      queryClient.invalidateQueries(listQueryKey);
      closeDialog();
    },
    onError: (err) => {
      const nextFieldErrors = normalizeFieldErrors(err?.fieldErrors);
      setFieldErrors(nextFieldErrors);
      setFormError(err?.message || "Failed to create team");
    },
  });

  const { mutate: updateExistingTeam, isPending: isUpdatePending } =
    useMutation({
      mutationKey: ["updateTeam"],
      mutationFn: ({ id, payload }) => updateTeam(id, payload),
      onSuccess: () => {
        queryClient.invalidateQueries(listQueryKey);
        closeDialog();
      },
      onError: (err) => {
        const nextFieldErrors = normalizeFieldErrors(err?.fieldErrors);
        setFieldErrors(nextFieldErrors);
        setFormError(err?.message || "Failed to update team");
      },
    });

  const { mutate: removeTeam, isPending: isDeletePending } = useMutation({
    mutationKey: ["deleteTeam"],
    mutationFn: (id) => deleteTeam(id),
    onSuccess: () => {
      queryClient.invalidateQueries(listQueryKey);
    },
    onError: (err) => {
      alert(err?.message || "Failed to delete team");
    },
  });

  const isSaving = isCreatePending || isUpdatePending;

  const submit = () => {
    const trimmedName = name.trim();
    const trimmedShort = shortName.trim();

    const nextFieldErrors = {};
    if (!trimmedName) {
      nextFieldErrors.name = "Name is required";
    }
    if (trimmedShort && trimmedShort.length > MAX_SHORT_NAME) {
      nextFieldErrors.shortName = `Short name must be ${MAX_SHORT_NAME} characters or less`;
    }
    const nextFileError = validatePngFile(imageField.file);
    if (nextFileError) {
      imageField.setFileError(nextFileError);
    }
    if (Object.keys(nextFieldErrors).length) {
      setFieldErrors(nextFieldErrors);
      return;
    }
    if (nextFileError) {
      return;
    }

    const payload = {
      name: trimmedName,
      shortName: trimmedShort ? trimmedShort : undefined,
      file: imageField.file || undefined,
      removeLogo: !imageField.file && imageField.isRemoved ? true : undefined,
    };

    setFormError("");
    setFieldErrors({});

    if (
      mode === "edit" &&
      editingTeam?.id !== null &&
      editingTeam?.id !== undefined
    ) {
      updateExistingTeam({ id: editingTeam.id, payload });
      return;
    }

    createNewTeam(payload);
  };

  const canShowRemoveImage = imageField.canShowRemove;

  const renderHeader = () => (
    <TableRow>
      <StyledTableCell
        sx={{
          width: LOGO_COL_WIDTH_PX,
          minWidth: LOGO_COL_WIDTH_PX,
          maxWidth: LOGO_COL_WIDTH_PX,
        }}
      >
        Logo
      </StyledTableCell>

      <StyledTableCell>
        <TableSortLabel
          active
          direction={sortOrder === "ASC" ? "asc" : "desc"}
          onClick={toggleSortOrder}
        >
          Name
        </TableSortLabel>
      </StyledTableCell>

      <StyledTableCell>Short name</StyledTableCell>

      <StyledTableCell>Actions</StyledTableCell>
    </TableRow>
  );

  const onDeleteClick = (team) => {
    if (
      window.confirm(
        `Delete team "${team?.name ?? team?.id}"? This action cannot be undone.`,
      )
    ) {
      removeTeam(team.id);
    }
  };

  const renderRow = (team) => (
    <TableRow key={team.id}>
      <TableCell
        sx={{
          width: LOGO_COL_WIDTH_PX,
          minWidth: LOGO_COL_WIDTH_PX,
          maxWidth: LOGO_COL_WIDTH_PX,
        }}
      >
        <TeamLogo name={team?.name} logo={team?.logo} />
      </TableCell>

      <TableCell>{team.name}</TableCell>

      <TableCell>{team.shortName || "-"}</TableCell>

      <TableCell>
        <IconButton aria-label="edit" onClick={() => openEdit(team)}>
          <EditIcon />
        </IconButton>

        <IconButton
          aria-label="delete"
          color="error"
          onClick={() => onDeleteClick(team)}
          disabled={isDeletePending}
        >
          <DeleteIcon />
        </IconButton>
      </TableCell>
    </TableRow>
  );

  const nameHelper = fieldErrors?.name || " ";
  const shortNameHelper =
    fieldErrors?.shortName || `Optional, max ${MAX_SHORT_NAME} chars`;

  return (
    <>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openCreate}
        >
          Create team
        </Button>
      </Box>

      <CustomTable
        name="teams"
        getItemsMethod={getTeams}
        renderHeader={renderHeader}
        renderRow={renderRow}
        columnCount={4}
        isSearchable
        searchLabel="Search by team name"
        queryKeyParts={[sortOrder]}
        queryParams={{ sortOrder }}
        pageResetKey={sortOrder}
      />

      <Dialog
        open={open}
        onClose={() => !isSaving && closeDialog()}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {mode === "edit" ? "Edit team" : "Create team"}
        </DialogTitle>

        <DialogContent>
          {formError ? (
            <Typography sx={{ mt: 1 }} color="error">
              {formError}
            </Typography>
          ) : null}

          <TextField
            fullWidth
            required
            label="Name"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (fieldErrors?.name) {
                setFieldErrors((prev) => ({ ...prev, name: undefined }));
              }
            }}
            error={Boolean(fieldErrors?.name)}
            helperText={nameHelper}
            sx={{ mt: 2 }}
          />

          <TextField
            fullWidth
            label="Short name"
            value={shortName}
            onChange={(e) => {
              setShortName(e.target.value);
              if (fieldErrors?.shortName) {
                setFieldErrors((prev) => ({ ...prev, shortName: undefined }));
              }
            }}
            error={Boolean(fieldErrors?.shortName)}
            helperText={shortNameHelper}
            inputProps={{ maxLength: MAX_SHORT_NAME }}
            sx={{ mt: 2 }}
          />

          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {mode === "edit"
                ? "Replace badge (optional)"
                : "Badge (optional)"}
            </Typography>

            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Button variant="outlined" component="label" disabled={isSaving}>
                Choose PNG
                <input
                  hidden
                  type="file"
                  accept="image/png"
                  ref={imageField.fileInputRef}
                  onChange={(e) => {
                    const next = e.target.files?.[0] || null;
                    imageField.onPickFile(next);
                  }}
                />
              </Button>

              {imageField.previewUrl ? (
                <img
                  src={imageField.previewUrl}
                  alt=""
                  style={{
                    width: 48,
                    height: 48,
                    objectFit: "contain",
                    display: "block",
                    borderRadius: 6,
                    background: "#f0f0f0",
                  }}
                />
              ) : mode === "edit" ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Current:
                  </Typography>
                  {imageField.isRemoved ? (
                    <Typography variant="body2" color="text.secondary">
                      Save to remove badge
                    </Typography>
                  ) : (
                    <TeamLogo
                      name={editingTeam?.name}
                      logo={editingTeam?.logo}
                    />
                  )}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No file selected
                </Typography>
              )}

              {imageField.file ? (
                <Button
                  variant="text"
                  color="inherit"
                  onClick={() => {
                    imageField.clearSelectedFile();
                  }}
                  disabled={isSaving}
                >
                  Clear
                </Button>
              ) : null}

              {canShowRemoveImage ? (
                <Button
                  variant="text"
                  color="error"
                  disabled={isSaving}
                  onClick={() => {
                    imageField.removeCurrent();
                  }}
                >
                  Remove badge
                </Button>
              ) : null}
            </Box>

            {imageField.fileError ? (
              <Typography
                variant="caption"
                color="error"
                sx={{ display: "block", mt: 0.5 }}
              >
                {imageField.fileError}
              </Typography>
            ) : null}
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={closeDialog} disabled={isSaving}>
            Cancel
          </Button>

          <Button
            onClick={submit}
            variant="contained"
            disabled={isSaving || Boolean(imageField.fileError)}
          >
            {isSaving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Teams;
